import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GlassPage } from '../components/chrome';
import { postJSON } from '../lib/api';

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organizationType: '',
    subject: location.state?.subject || '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await postJSON('/contact', {
        name: formData.name,
        email: formData.email,
        organization: formData.organizationType || null,
        message: `[${formData.subject}] ${formData.message}`,
      });
      setIsSubmitted(true);

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '', organizationType: '', subject: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (err) {
      console.error(err);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const inputClass = 'glass-input w-full px-6 py-4';
  const labelClass = 'block text-[10px] font-bold uppercase tracking-widest mb-3 text-zinc-400';

  return (
    <GlassPage>
      {/* Hero Section */}
      <section className="relative flex flex-col justify-center px-6 md:px-12 pt-40 pb-12">
        <div className="max-w-4xl mx-auto w-full">
          <h1 className="text-[12vw] md:text-[6vw] font-bold leading-[0.9] tracking-tighter mb-10 uppercase text-zinc-900">
            Contact<br/>Us
          </h1>
          <p className="text-lg md:text-xl text-zinc-500 font-medium max-w-2xl">
            Have a question or want to get in touch? Send us a message and we'll get back to you as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 md:py-16 px-4 md:px-6">
        <div className="glass max-w-4xl mx-auto px-8 md:px-12 py-12 md:py-16">
          {isSubmitted ? (
            <div className="rounded-3xl border border-green-500/20 bg-green-500/10 p-8 text-center">
              <h3 className="text-2xl font-bold mb-4 text-green-700">Message Sent Successfully!</h3>
              <p className="text-zinc-500">Thank you for contacting us. We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="your@email.com"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Organization *</label>
                  <select
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                    required
                    className={`${inputClass} appearance-none cursor-pointer`}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2318181b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1.5rem center'
                    }}
                  >
                    <option value="" disabled>Select type</option>
                    <option value="individual">Individual</option>
                    <option value="investor">Investor</option>
                    <option value="company">Company Representative</option>
                    <option value="partner">Potential Partner</option>
                    <option value="media">Media/Press</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="What is this about?"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={8}
                  className={`${inputClass} resize-none`}
                  placeholder="Tell us what you'd like to discuss..."
                />
              </div>

              <button type="submit" disabled={isSubmitting} className={`btn-dark w-full md:w-auto px-16 py-5 ${isSubmitting ? 'opacity-60 cursor-wait' : ''}`}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitError && (
                <p className="text-[10px] uppercase tracking-widest text-red-500 font-bold">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          )}
        </div>
      </section>
    </GlassPage>
  );
};

export default Contact;
